/* global JSX */
import { Box, Grid } from "@mui/material";
import * as React from "react";
import { Base } from "ui/Scripts/source/framework/base";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "../../../framework/appUtils";
import { IListFilterItem } from "../../../models/common/listFilterItem";
import { infiniteListPageSize } from "../../../models/common/webConsts";
import { ILogListItem } from "../../../models/log/logListItem";
import { ILogListItems } from "../../../models/log/logListItems";
import * as logService from "../../../models/services/logService";
import { SettingsButton } from "../../framework/Button/SettingsButton";
import { GridRow } from "../../framework/Grid/GridRow";
import { IListUsingComponentState, List } from "../../framework/List/List";
import { ListActionButtons } from "../../framework/List/ListActionButtons";
import { ListCardField } from "../../framework/List/ListCardField";
import { MainListContainer } from "source/components/framework/List/MainListContainer";
import { ListDetailsButton } from "../../framework/List/ListDetailsButton";
import { ListLinkValue } from "../../framework/List/ListLinkValue";
import { ListSearchContainer } from "../../framework/List/ListSearchContainer";
import { ListSearchFilter } from "../../framework/List/ListSearchFilter";
import { ListToggleButtons } from "../../framework/List/ListToggleButtons";
import { PageListActionContainer } from "../../framework/List/PageListActionContainer";
import { PageTitle } from "../../framework/Page/PageTitle";
import { IPageNavMenuItem } from "source/components/framework/PageNav/PageNavMenuItems";
import { LogDialog } from "../LogDialog";

export interface ILogListProp {
    companyId?: string;
    disableGlobalLoading?: boolean;
    preloaded?: boolean;
    waitingPreloadedData?: boolean;
    savedFilter: IListFilterItem;
    list?: ILogListItems;
    listFilterIsSet: boolean;
    types?: string[];
    timeSpan?: string;
    dataId?: string;
    startDateStr?: string;
    endDateStr?: string;
    sortColumn: string;
    sortOrderIsAsc: boolean;
    titleSuffix?: JSX.Element;
    reportTabs: React.ReactNode;
    settingsActions: IPageNavMenuItem[];
    onSetSortData: (sortColumn: string, sortOrderIsAsc: boolean) => void;
    onOpenFiltersDialog: () => void;
    onToggleSettingsDialog: () => void;
}

interface ILogListState extends IListUsingComponentState<ILogListItem> {
    // List
    intialized: boolean;
    isLoading: boolean;
    waitingPreloadedData: boolean;
    pageSize: number;
    page: number;
    filter: string;
    types: number[];
    timeSpan: number;
    startDate: number;
    endDate: number;
    sortColumn: string;
    sortOrderIsAsc: boolean;
    listModeIsLine: boolean;
    checkedIds: string[];
}

export class LogList extends React.Component<ILogListProp, ILogListState> {
    constructor(props: ILogListProp) {
        super(props);
        this.state = {
            intialized: props.list && !!props.preloaded,
            isLoading: false,
            waitingPreloadedData: !Base.isNullOrUndefined(props.waitingPreloadedData) ? props.waitingPreloadedData : (props.list ? false : props.preloaded),
            page: props.list ? props.list.page : 1,
            hasMore: props.list ? props.list.hasMore : true,
            filter: "",
            types: props.types ? props.types.filter(i => i !== "0").map(i => parseInt(i, 10)) : [],
            timeSpan: props.timeSpan ? parseInt(props.timeSpan, 10) : 0,
            startDate: props.startDateStr ? props.startDateStr.toUtcDate().getTime() : 0,
            endDate: props.endDateStr ? props.endDateStr.toUtcDate().getTime() : 0,
            pageSize: props.list ? props.list.pageSize : infiniteListPageSize,
            sortColumn: props.sortColumn,
            sortOrderIsAsc: props.sortOrderIsAsc,
            listModeIsLine: true,
            items: props.list ? props.list.items : [],
            selectedId: null,
            checkedIds: [],
            detailsItem: null,
            showEditDialog: false,
            editDialogId: "",
            editDialogHasPrevious: false,
            editDialogHasNext: false
        };
    }

    searchItems = async (companyId: string, pageSize: number, page: number, filter: string, types: number[], timeSpan: number, startDate: number, endDate: number, sortColumn: string, sortOrderIsAsc: boolean,
        resetItems: boolean, refreshList: boolean, moveNext: boolean = false) => {
        const props = this.props;
        this.setState({ isLoading: true });
        const result = await AppUtils.callService(() => logService.getLogListItems(companyId, !refreshList ? pageSize : (page * pageSize), !refreshList ? page : 1, filter, types, timeSpan, startDate, endDate, sortColumn, sortOrderIsAsc),
            false, props.disableGlobalLoading, () => this.setState({ isLoading: false }));
        if (!result) return;
        const items = List.getListItems(this.state.items, this.state.selectedId, this.state.checkedIds, result.items, resetItems, refreshList);
        let selectedId = items.selectedId;
        let index = items.items.findIndex(i => i.id === selectedId);
        let detailsItem = index > -1 ? items.items[index] : null;
        if (moveNext && detailsItem && index < items.items.length - 1) {
            index = index + 1;
            detailsItem = items.items[index];
            selectedId = items.items[index].id;
        }
        this.setState({
            intialized: true,
            page: !refreshList ? result.page : page,
            hasMore: result.hasMore,
            filter,
            types,
            timeSpan,
            startDate,
            endDate,
            sortColumn,
            sortOrderIsAsc,
            items: items.items,
            selectedId,
            checkedIds: items.checkedIds,
            detailsItem,
            editDialogHasPrevious: index > 0,
            editDialogHasNext: index < items.items.length - 1 || result.hasMore,
        });
        this.props.onSetSortData(sortColumn, sortOrderIsAsc);
    };

    refreshList = () => {
        const state = this.state;
        this.searchItems(this.props.companyId, state.pageSize, state.page, state.filter, state.types, state.timeSpan, state.startDate, state.endDate, state.sortColumn, state.sortOrderIsAsc, false, true);
    };

    componentDidMount(): void {
        const state = this.state;
        if (this.props.preloaded) return;
        this.searchItems(this.props.companyId, state.pageSize, state.page, state.filter, state.types, state.timeSpan, state.startDate, state.endDate, state.sortColumn, state.sortOrderIsAsc, false, false);
    }

    componentDidUpdate(prevProps: ILogListProp, prevState: ILogListState) {
        const props = this.props;
        const state = this.state;
        if (state.waitingPreloadedData) {
            if (!props.list || (props.dataId ?? "") === (prevProps.dataId ?? "")) return;
            this.setState({
                intialized: true,
                waitingPreloadedData: false,
                page: props.list.page,
                hasMore: props.list.hasMore,
                items: props.list.items,
                pageSize: props.list.pageSize,
                sortColumn: props.list.sortColumn,
                sortOrderIsAsc: props.list.sortOrderIsAsc
            });
        } else {
            const prevDataId = prevProps.dataId ? prevProps.dataId : "";
            const dataId = props.dataId ? props.dataId : "";
            const prevCompanyId = prevProps.companyId ? prevProps.companyId : "";
            const companyId = props.companyId ? props.companyId : "";
            const prevTypes = prevProps.types ? prevProps.types.filter(i => i !== "0").map(i => parseInt(i, 10)) : [];
            const types = props.types ? props.types.filter(i => i !== "0").map(i => parseInt(i, 10)) : [];
            const prevTimeSpan = prevProps.timeSpan ? parseInt(prevProps.timeSpan, 10) : 0;
            const timeSpan = props.timeSpan ? parseInt(props.timeSpan, 10) : 0;
            const prevStartDate = prevProps.startDateStr ? prevProps.startDateStr.toUtcDate().getTime() : 0;
            const startDate = props.startDateStr ? props.startDateStr.toUtcDate().getTime() : 0;
            const prevEndDate = prevProps.endDateStr ? prevProps.endDateStr.toUtcDate().getTime() : 0;
            const endDate = props.endDateStr ? props.endDateStr.toUtcDate().getTime() : 0;
            const prevSortColumn = prevProps.sortColumn;
            const sortColumn = props.sortColumn;
            const prevSortOrderIsAsc = prevProps.sortOrderIsAsc;
            const sortOrderIsAsc = props.sortOrderIsAsc;
            if (dataId === prevDataId && companyId === prevCompanyId &&
                Base.equalStrArrays(prevTypes.map(i => i.toString(10)), types.map(i => i.toString(10))) &&
                prevTimeSpan === timeSpan && prevStartDate === startDate && prevEndDate === endDate &&
                prevSortColumn === sortColumn && prevSortOrderIsAsc === sortOrderIsAsc) return;
            this.searchItems(companyId, state.pageSize, state.page, state.filter, types, timeSpan, startDate, endDate, sortColumn, sortOrderIsAsc, true, false);
        }
    }

    changeFilter = (filter: string) => {
        const state = this.state;
        this.searchItems(this.props.companyId, state.pageSize, 1, filter, state.types, state.timeSpan, state.startDate, state.endDate, state.sortColumn, state.sortOrderIsAsc, true, false);
    };

    handleSearchLines = (sortColumn: string, sortOrderIsAsc: boolean, nextPage: boolean) => {
        const state = this.state;
        this.searchItems(this.props.companyId, state.pageSize, !nextPage ? 1 : state.page + 1, state.filter, state.types, state.timeSpan, state.startDate, state.endDate, sortColumn, sortOrderIsAsc, !nextPage, false);
    };

    handleSetSelectedIds = (selectedId: string, checkedIds: string[]) => {
        this.setState({
            selectedId,
            checkedIds,
            detailsItem: this.state.items.find(i => i.id === selectedId)
        });
    };

    handleListModeToggle = (_event: React.MouseEvent<HTMLElement>, listModeIsLine: boolean) => {
        this.setState({
            listModeIsLine,
        });
    };

    handleOpenLine = (id: string) => {
        this.setState(List.getShowEditDialogStateChange(this.state, id));
    };

    handleLogDialogCancel = () => {
        this.setState({
            showEditDialog: false
        });
    };

    setEditDialogItemByIndex = (index: number) => {
        const state = this.state;
        this.setState(List.getEditDialogItemByIndexStateChange(this.state, index, () => this.searchItems(this.props.companyId, state.pageSize, state.page + 1, state.filter, state.types, state.timeSpan, state.startDate, state.endDate, state.sortColumn, state.sortOrderIsAsc, false, false, true)));
    };

    render() {
        const props = this.props;
        const state = this.state;
        return (
            <>
                <PageTitle title={Translations.Log + (props.savedFilter ? " - " + props.savedFilter.title : (props.listFilterIsSet || state.filter ? " - " + Translations.Filtered : ""))}>
                    {props.settingsActions.length > 0 &&
                        <SettingsButton onClick={props.onToggleSettingsDialog}>
                            {Translations.Settings}
                        </SettingsButton>
                    }
                </PageTitle>
                <MainListContainer>
                    <PageListActionContainer
                        leftItems={
                            props.reportTabs
                        }
                        rightItems={
                            <>
                                <ListActionButtons
                                    filterIsSet={props.listFilterIsSet}
                                    onFilter={props.onOpenFiltersDialog}
                                />
                                <ListToggleButtons value={state.listModeIsLine} onChange={this.handleListModeToggle} />
                            </>
                        }
                    />
                    <ListSearchContainer>
                        <ListSearchFilter
                            searchFilter={state.filter}
                            onSearchClick={this.changeFilter}
                        />
                    </ListSearchContainer>
                    <List
                        intialized={state.intialized}
                        isLoading={state.isLoading}
                        hasMore={state.hasMore}
                        globalLoadingInUse={!props.disableGlobalLoading}
                        height="calc(100vh - 296px)" //Main List
                        header={state.listModeIsLine
                            ? {
                                columns: [
                                    { size: 2, column: "modifyDate", content: Translations.Time },
                                    { size: 2, column: "type", content: Translations.LogEventType },
                                    { size: 2, column: "companyCode", content: Translations.Company },
                                    { size: 2, column: "userCode", content: Translations.UserName },
                                    { size: 4, column: "operation", content: Translations.Operation }]
                            }
                            : null}
                        lines={state.listModeIsLine
                            ? state.items.map((item) => {
                                return {
                                    columns: [
                                        { size: 2, column: "modifyDate", content: <ListLinkValue onClick={() => this.handleOpenLine(item.id)}>{Base.timeToDateTimeStr(item.modifyDate, true)}</ListLinkValue> },
                                        { size: 2, column: "type", content: item.typeStr },
                                        { size: 2, column: "companyCode", content: item.companyCode },
                                        { size: 2, column: "userCode", content: item.userCode },
                                        { size: 4, column: "operation", content: item.operation }],
                                    id: item.id
                                };
                            })
                            : null
                        }
                        cards={!state.listModeIsLine
                            ? state.items.map((item) => {
                                return {
                                    sm: 4,
                                    rows: [
                                        {
                                            cols: [
                                                {
                                                    size: 6,
                                                    title: Translations.Time,
                                                    value: <Box display="flex" alignItems="center"><ListLinkValue onClick={() => this.handleOpenLine(item.id)}>{Base.timeToDateTimeStr(item.modifyDate, true)}</ListLinkValue></Box>
                                                },
                                                { size: 6, title: Translations.AType, value: item.typeStr }
                                            ]
                                        },
                                        {
                                            classes: "row",
                                            cols: [
                                                { size: 6, title: Translations.Company, value: item.companyCode },
                                                { size: 6, title: Translations.User, value: item.userCode }
                                            ]
                                        },
                                        {
                                            classes: "row",
                                            cols: [
                                                { title: Translations.Operation, value: item.operation }
                                            ]
                                        }],
                                    id: item.id
                                };
                            })
                            : null
                        }
                        lineDetails={state.detailsItem
                            ? <Box position="relative">
                                <GridRow>
                                    <Grid item sm={4}>
                                        <ListCardField title={Translations.Time} value={Base.timeToDateTimeStr(state.detailsItem.modifyDate, true)} />
                                    </Grid>
                                    <Grid item sm={4}>
                                        <ListCardField title={Translations.AType} value={state.detailsItem.typeStr} />
                                    </Grid>
                                    <Grid item sm={4}>
                                        <ListCardField title={Translations.Company} value={state.detailsItem.companyCode} />
                                    </Grid>
                                    <Grid item sm={4}>
                                        <ListCardField title={Translations.IpAddress} value={state.detailsItem.callerIp} />
                                    </Grid>
                                    <Grid item sm={4}>
                                        <ListCardField title={Translations.BrowserInfo} value={state.detailsItem.browserInfo} />
                                    </Grid>
                                    <Grid item sm={4}>
                                        <ListCardField title={Translations.User} value={state.detailsItem.userCode} />
                                    </Grid>
                                    <Grid item sm={4}>
                                        <ListCardField title={Translations.Operation} value={state.detailsItem.operation} />
                                    </Grid>
                                    <Grid item sm={4}>
                                        <ListCardField title={Translations.Action} value={state.detailsItem.action} />
                                    </Grid>
                                    <Grid item sm={4}>
                                        <ListCardField title={Translations.Version} value={state.detailsItem.version} />
                                    </Grid>
                                </GridRow>
                                <ListDetailsButton
                                    openReadOnly={true}
                                    onClick={() => this.handleOpenLine(state.selectedId)}
                                />
                            </Box>
                            : null}
                        sortColumn={state.sortColumn}
                        sortOrderIsAsc={state.sortOrderIsAsc}
                        selectedId={state.selectedId}
                        checkedIds={state.checkedIds}
                        onSetSelectedIds={this.handleSetSelectedIds}
                        onSearchLines={this.handleSearchLines}
                        onOpenLine={this.handleOpenLine}
                    />
                </MainListContainer>
                {state.showEditDialog &&
                    <LogDialog
                        item={state.detailsItem}
                        hasPrevious={state.editDialogHasPrevious}
                        hasNext={state.editDialogHasNext}
                        onCancel={this.handleLogDialogCancel}
                        onPrevious={() => List.moveToPrevious(state, this.setEditDialogItemByIndex)}
                        onNext={() => List.moveToNext(state, this.setEditDialogItemByIndex)}
                    />
                }
            </>
        );
    }
}